import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { AbstractComponent, Channel, ChannelService, Integration, IntegrationService } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { Agent, TrendGPTMakerAPIService } from '@trendbuild/trend-gpt-maker-api';

interface ItemOption {
  label: string;
  value: string;
}

interface AgentWithMetadata extends Agent {
  metadata?: {
    channels?: { _id: string, name?: string, type?: string }[]
  }
}


@Component({
    selector: 'app-trendgpt-integration-agents-form',
    templateUrl: './trendgpt-integration-agents-form.v2.component.html',
    // templateUrl: './trendgpt-integration-agents-form.component.html',
    styleUrls: ['./trendgpt-integration-agents-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TrendGPTAgentsFormComponent extends AbstractComponent implements OnInit {

  private trendGPTMakerAPIService: TrendGPTMakerAPIService = new TrendGPTMakerAPIService();

  assistants: Agent[] = [];
  assistant: AgentWithMetadata = {} as AgentWithMetadata;
  models: ItemOption[] = [];
  selectedModel: string | null = null;
  channels: Channel[] = [];
  selectedChannels: string[] = [];
  selectedChannel: Channel;
  integration: Integration;

  breadcrumb = [
    { href: '/integrations', label: 'integrations.breadcrumb.integrations' },
    { href: '/integrations/trendgpt/list', label: 'integrations.breadcrumb.trendgpt' },
    { label: 'integrations.breadcrumb.addAgent' },
  ]

  constructor(
    injector: Injector,
    private channelService: ChannelService,
    private integrationService: IntegrationService,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit() {
    this.isNew = true;
    const idIntegration = this.getParam('idintegration');
    const idAssistant = this.getParam('idagent');

    this.loadChannels();

    if (idIntegration) {
      this.getIntegrationById(idIntegration, idAssistant);
    }

    if (idAssistant) {
      this.isNew = false;
    }

    if (!this.isNew) {
      this.breadcrumb[1].label = 'Editar Integração';
    }
  }

  getIntegrationById(idIntegration: string, idAssistant: string) {
    this.integrationService.getById(idIntegration)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (integration: Integration) => {
          this.integration = integration;
          this.loadChannels(idAssistant);
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  getAssistantById(idAssistant: string) {
    this.trendGPTMakerAPIService.getById(idAssistant)
      .then((agent: Agent) => {
        const matchedAgentMetadata = this.integration?.metadata["agents"]?.find((a: any) => a.id === idAssistant);

        this.assistant = {
          ...agent,
          metadata: {
            channels: matchedAgentMetadata?.channels || []
          }
        };

        if (this.channels?.length) {
          this.populateForm(this.assistant);
        }
      })
      .catch((err) => {
        this.alertService.error(err.error?.message || this.t('integrations.agentsForm.errorLoadAssistant'));
      });
  }

  loadChannels(idAssistant?: string) {
    this.channelService.getList().subscribe({
      next: (channels) => {
        this.channels = channels;
        if (idAssistant) {
          this.getAssistantById(idAssistant);
        }
      },
      error: () => {
        this.alertService.error(this.t('integrations.agentsForm.errorLoadChannels'));
      },
    });
  }

  onCommunicationSelection(event: string) {
    this.formGroup.get('communicationType')?.setValue(event);
  }

  onAgentTypeSelection(event: string) {
    this.formGroup.get('type')?.setValue(event);
  }

  onChannelsSelection(selectEl: HTMLSelectElement) {
    const selected = Array.from(selectEl.selectedOptions).map(o => o.value);
    this.formGroup.get('channels')?.setValue(selected);
  }

  setupForm() {
    this.formGroup = new FormGroup({
      name: new FormControl('', Validators.required),
      description: new FormControl('', Validators.required),
      jobName: new FormControl('', Validators.required),
      jobSite: new FormControl('', Validators.required),
      jobDescription: new FormControl('', Validators.required),
      communicationType: new FormControl('', Validators.required),
      type: new FormControl('', Validators.required),
      channels: new FormControl([]),
    });

    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.assistant, {
        name: value.name,
        behavior: value.description,
        jobName: value.jobName,
        jobSite: value.jobSite,
        jobDescription: value.jobDescription,
        communicationType: value.communicationType,
        type: value.type,
      });
    });
  }

  populateForm(assistant: AgentWithMetadata) {
    const channels = assistant.metadata?.channels || [];
    this.selectedChannels = channels.map((channel: Channel) => channel._id);

    this.formGroup.patchValue({
      name: assistant.name || '',
      description: assistant.jobDescription || '',
      jobName: assistant.jobName || '',
      jobSite: assistant.jobSite || '',
      jobDescription: assistant.jobDescription || '',
      communicationType: assistant.communicationType,
      type: assistant.type,
      channels: this.selectedChannels,
    });
  }

  async onSubmit() {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }

    this.loading = true;

    const formValue = this.formGroup.value;

    const channels = this.formGroup.get('channels')?.value.map((idChannel: string) => {
      const channel = this.channels.find(c => c._id === idChannel);
      return { _id: channel._id, name: channel.name, type: channel.type };
    });

    const agent: Agent = {
      name: formValue.name,
      behavior: formValue.description,
      jobName: formValue.jobName,
      jobSite: formValue.jobSite,
      jobDescription: formValue.jobDescription,
      communicationType: formValue.communicationType,
      type: formValue.type,
    };

    try {
      if (this.isNew) {
        const agentCreated = await this.trendGPTMakerAPIService.create(agent);
        const returno = await this.setupChannelsAssistant(agentCreated.id, channels, agentCreated.name);
      } else {
        await this.trendGPTMakerAPIService.updateById(this.assistant.id, agent);
        await this.setupChannelsAssistant(this.assistant.id, channels, this.assistant.name);
      }

      this.alertService.success(this.t('integrations.agentsForm.successAssistantSaved'));
      this.goBack();

    } catch (err) {
      this.alertService.error(err.error?.message || this.t('integrations.agentsForm.errorSaveAssistant'));
    } finally {
      this.loading = false;
    }
  }

  setupChannelsAssistant(agentId: string, channels: Channel[], agentName: string) {
    const metadata = this.integration.metadata || {};
    let agents = metadata['agents'] || [];

    const index = agents.findIndex((a: any) => a.id === agentId);

    if (index >= 0) {
      agents[index] = { name: agentName, id: agentId, createdAt: new Date(), channels };
    } else {
      agents.push({ name: agentName, id: agentId, createdAt: new Date(), channels });
    }

    metadata['agents'] = agents;

    const updatedIntegration = { ...this.integration, metadata };

    return new Promise<void>((resolve, reject) => {
      this.integrationService.update(this.integration._id, updatedIntegration)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => resolve(),
          error: (err) => {
            this.alertService.error(err.error.message);
            reject(err);
          },
        });
    });
  }
}
